import type { PageContextDescriptor } from './types'

/**
 * Absolute path patterns (React Router syntax) → context posture.
 *
 * Every route declared in `routes/index.tsx` (engineer) and
 * `operator/routes.tsx` (technician) must appear here — the coverage
 * test fails CI on any undeclared path. Adding a page without deciding
 * its context posture is intentionally a red build.
 *
 * AXI-4791 only uses `automatic` | `none`. Page-specific registrations
 * (AXI-4792) keep the descriptor as `automatic` and push richer entries
 * through {@link useChatAppContext}.
 */
export const PAGE_CONTEXT_BY_PATH: Readonly<Record<string, PageContextDescriptor>> = {
  // ── Engineer standalone ────────────────────────────────────────────
  '/integrations/connect': 'none',
  '/integrations/connect/success': 'none',

  // ── Engineer main ──────────────────────────────────────────────────
  '/graph': 'automatic',

  '/plant': 'automatic',
  '/plant/equipment': 'automatic',
  '/plant/equipment/sites/:siteId': 'automatic',
  '/plant/equipment/sites/:siteId/lines/:lineId': 'automatic',
  '/plant/equipment/items/:equipmentId': 'automatic',
  '/plant/processes': 'automatic',
  '/plant/areas': 'automatic',
  '/plant/areas/:areaId': 'automatic',
  '/plant/compute': 'automatic',
  '/plant/compute/hosts/:computeId': 'automatic',
  '/plant/queries': 'automatic',
  '/plant/queries/:queryId': 'automatic',
  '/plant/plc': 'automatic',
  '/plant/dcs': 'automatic',
  '/plant/robotics': 'automatic',
  '/plant/schedules': 'automatic',
  '/plant/schedules/:nodeId': 'automatic',
  '/plant/alarms': 'automatic',
  '/plant/safety': 'automatic',
  '/plant/safety/groups/:sectionId': 'automatic',
  '/plant/safety/solvers/:solverId': 'automatic',
  '/plant/safety/flds/:fldId': 'automatic',
  '/plant/safety/points/:pointId': 'automatic',
  '/plant/safety/:functionId': 'automatic',
  '/plant/entity/:id': 'automatic',
  '/plant/model': 'automatic',
  '/plant/*': 'none', // redirect → equipment

  '/ingest': 'automatic',
  '/ingest/jobs/:jobId/agent': 'automatic',
  '/ingest/jobs/:jobId/tabular': 'automatic',
  '/ingest/configs/:name': 'automatic',
  '/ingest/configs/:name/builds/:buildJobId': 'automatic',
  '/ingest/configs/:name/builds/:buildJobId/tool-calls': 'automatic',
  '/chat': 'automatic',
  '/chat/memory': 'none',
  '/chat/:conversationId': 'automatic',
  '/dashboards': 'automatic',
  '/dashboards/templates': 'automatic',
  '/dashboards/templates/f/*': 'automatic',
  '/dashboards/templates/*': 'automatic',
  '/dashboards/f/*': 'automatic',
  '/dashboards/*': 'automatic',
  '/dashboards/autogenerate': 'automatic',
  '/artifacts/:artifactId': 'automatic',
  // Leftover `/docs*` is redirect-only (LegacyDocsRedirect).
  '/problems': 'automatic',
  '/problems/:occurrenceId': 'automatic',
  '/tasks': 'automatic',
  '/runs': 'automatic',
  '/configs': 'none', // redirect → configure
  '/configure': 'automatic',

  '/agents': 'none',
  '/agents/*': 'none',

  '/tags': 'automatic',
  '/notifications': 'automatic',
  '/settings': 'none',
  '/users': 'none',
  '/users/role-assignments': 'none',
  '/users/groups': 'none',
  '/feedback': 'none',
  '/usage': 'none',
  '/branches/merge': 'automatic',
  '/branches/merges': 'automatic',
  // Documents library — chat needs the open doc so "update this doc" resolves.
  '/documents': 'automatic',
  '/documents/templates': 'automatic',
  '/documents/templates/f/*': 'automatic',
  '/documents/templates/*': 'automatic',
  '/documents/f/*': 'automatic',
  '/documents/*': 'automatic',
  // Leftover `/docs*` redirects to `/documents`.
  '/docs': 'none',
  '/docs/*': 'none',
  // Static User Guide; nothing plant-specific to infer from the URL.
  '/guide': 'none',
  '/releases': 'none',
  '/legal/privacy-policy': 'none',
  '/legal/terms-of-use': 'none',
  '/agent-memory': 'none',

  // Catch-all NotFound — no useful page context.
  '*': 'none',

  // ── Technician (operator) ──────────────────────────────────────────
  // Index route is recorded as `/`.
  '/': 'automatic',
  '/work-orders/:id': 'automatic',
  // Operator settings shares the engineer `none` posture; absolute path
  // collides with engineer `/settings` which is already `none`.
  '/equipment': 'none',
  '/procedures': 'none',
  '/procedures/:alarmId': 'none',
  '/handoff': 'none'
}

/** Look up the descriptor for an absolute path pattern; undefined if undeclared. */
export function getPageContextDescriptor(pathPattern: string): PageContextDescriptor | undefined {
  return PAGE_CONTEXT_BY_PATH[pathPattern]
}
